import { useQuery } from "@apollo/client";
import { gql } from "_graphql-types/gql";
import * as types from "_graphql-types/graphql";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getGlobalSearchFirmIds,
  getGlobalSearchTerm,
} from "Reducers/globalSearchV2/globalSearchV2.selectors";

export const ENGAGEMENT_SEARCH = gql(/* GraphQL */ `
  query EngagementSearch(
    $filter: FirmEngagementFilter
    $page: PageInput!
    $sort: [FirmEngagementSort!]
  ) {
    firmEngagementList(filter: $filter, page: $page, sort: $sort) {
      items {
        ...FirmEngagementTile
      }
      total
      nextPage {
        offset
        limit
        hasMore
      }
    }
  }
`);

/**
 * Builds the FirmEngagementFilter for global search. Engagements are
 * firm-scoped, so the global firm-level filter (FIRM_ENTITY) drives which
 * engagements appear. With no firm selected, all entitled engagements show.
 */
export function useEngagementSearchVariables(page?: types.PageInput) {
  const firmIds = useSelector(getGlobalSearchFirmIds);
  const q = useSelector(getGlobalSearchTerm);

  return useMemo(
    () => ({
      filter: {
        ...(firmIds.length > 0 ? { firmIds } : {}),
        ...(q ? { q } : {}),
      },
      // When a text term is present, rank by name-search relevance; otherwise
      // fall back to most-recently-modified.
      sort: [
        q
          ? {
              field: types.FirmEngagementSortEnum.NameSearchRank,
              order: types.SortInput.Desc,
            }
          : {
              field: types.FirmEngagementSortEnum.ModifyDate,
              order: types.SortInput.Desc,
            },
      ],
      page: {
        offset: page?.offset ?? 0,
        limit: page?.limit ?? 24,
      },
    }),
    [firmIds, q, page?.offset, page?.limit]
  );
}

export function useGlobalEngagementSearch(page?: types.PageInput) {
  const variables = useEngagementSearchVariables(page);

  const { data, loading, error, fetchMore } = useQuery(ENGAGEMENT_SEARCH, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const result = data?.firmEngagementList
    ? { ...data.firmEngagementList, fetchMore }
    : undefined;

  return { variables, loading, error, result };
}
